import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';

/**
 * El formulario de ingreso. Lo comparten la instancia nueva (con la tapa del
 * libro) y la vieja (con el cartel de mudanza): el ingreso tiene que funcionar
 * igual en las dos, lo que cambia es la identidad alrededor.
 */
export function FormularioIngreso() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ayudaClave, setAyudaClave] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (error) {
      toast({
        title: 'No pudimos ingresar',
        description: error instanceof Error ? error.message : 'Revise el correo y la contraseña.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="email">Correo electrónico</Label>
        <Input
          id="email"
          type="email"
          autoComplete="username"
          placeholder="nombre@betsupplier.co"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
          className="h-11"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <Label htmlFor="password">Contraseña</Label>
          {/* py-2/-my-2: el texto es chico, pero el área de toque llega a ~40px
              sin mover el renglón. */}
          <button
            type="button"
            onClick={() => setAyudaClave((v) => !v)}
            aria-expanded={ayudaClave}
            aria-controls="ayuda-clave"
            className="-my-2 py-2 text-xs font-medium text-amber-700 underline-offset-4 hover:underline"
          >
            ¿Olvidó su contraseña?
          </button>
        </div>
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={isLoading}
          className="h-11"
        />
        {/*
          No hay recuperación por correo: la app no tiene envío de mails
          configurado (GMAIL_* está en "not-set" en el wrangler.toml). Lo que SÍ
          funciona hoy es que un administrador le asigne una clave nueva desde el
          panel de Usuarios (PUT /usuarios/:id con `password`). Esto dice eso, en
          vez de un botón que prometa un mail que nunca va a llegar.
        */}
        {ayudaClave && (
          <p
            id="ayuda-clave"
            className="rounded-md bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-950 [text-wrap:pretty]"
          >
            Para restablecerla, comuníquese con un administrador de BET Supplier:
            le asigna una contraseña nueva desde el panel de usuarios y puede
            ingresar de inmediato.
          </p>
        )}
      </div>

      {/* Hundido al apretar (0.96). `transition-[…]` con propiedades
          explícitas, nunca `transition-all`. */}
      <Button
        type="submit"
        disabled={isLoading}
        className="h-11 w-full text-[15px] font-semibold transition-[background-color,transform] duration-150 active:scale-[0.96]"
      >
        {isLoading ? 'Ingresando…' : 'Ingresar'}
      </Button>
    </form>
  );
}
